import { Co6TransactionTypes, InternalTransferSubmit, RegularISubmit } from '@interfaces';
import { InternalTransferReceiptDTO } from '@interfaces/models';
import { createSelector } from '@ngrx/store';
import {
  getDecedentInfoState,
  getFeatureToggleState,
  getInternalTransferState,
  getOpenAccountState,
  getSelectedClientAccountsState,
  getSubmitState,
  getTriageState,
} from '@store/reducers';
import { getClientName } from '@store/reducers/personal-data/personal-data.reducer';
import { ISubmitState } from '@store/reducers/submit/submit.reducer';
import {
  selectClientPersonalInfoData,
  selectEmploymentFeature,
  selectPersonalAddressFeature,
  selectPersonalPhone,
} from 'vg-interaction-model/dist';

import { selectBeneficiaryState } from '../beneficiaries/beneficiaries.selectors';
import { selectSelectedPositions } from '../cost-basis/cost-basis.selectors';
import { selectEstateState } from '../estate/estate.selectors';
import { selectGiftingState } from '../gifting/gifting.selectors';
import { selectEnrollPageHTML } from '../open-choice/open-choice.selectors';
import { selectOTPVerificationStatus } from '../otp/otp.selectors';
import {
  selectClientName,
  selectClientSegment,
  selectPersonalDataState,
} from '../personal-data/personal-data.selectors';
import { selectInternalTransferReceiptData } from '../receipt/internal-transfer/internal-transfer-receipt.selectors';
import { selectReceiptData } from '../receipt/receipt.selectors';
import { selectTCFTrustState } from '../tcf-trust/tcf-trust.selectors';
import { selectTrustState } from '../trust/trust.selectors';
import { selectTrustTaxInfoState } from '../trust-tax-info/trust-tax-info.selector';
import { checkForFlowTypes, createReceiptDTO } from './submit.helpers';

export const selectIsSubmitInProgress = createSelector(getSubmitState, (state: ISubmitState) => {
  return state.isSubmitInProgress;
});

export const selectIsStp = createSelector(getSubmitState, (state: ISubmitState) => {
  return state.isStp;
});

export const getSubmitPayload = createSelector(
  selectClientPersonalInfoData,
  selectPersonalDataState,
  getDecedentInfoState,
  getFeatureToggleState,
  getTriageState,
  getOpenAccountState,
  selectReceiptData,
  selectGiftingState,
  selectSelectedPositions,
  getSelectedClientAccountsState,
  selectEstateState,
  selectTrustState,
  selectTrustTaxInfoState,
  selectTCFTrustState,
  selectOTPVerificationStatus,
  selectEnrollPageHTML,
  selectEmploymentFeature,
  selectPersonalAddressFeature,
  selectPersonalPhone,
  selectBeneficiaryState,
  (
    clientPersonalInfoData,
    personalData,
    decedentInfo,
    featureToggles,
    triageState,
    openAccountState,
    receiptDTO,
    giftingState,
    selectedPositions,
    selectedClientAccounts,
    estateInfo,
    trustState,
    trustTaxInfoState,
    tcfTrustState,
    otpVerificationStatus,
    enrollPageHTML,
    employmentFeature,
    address,
    phone,
    beneficiaryState,
    // eslint-disable-next-line max-params
  ): RegularISubmit => {
    const transactionType = checkForFlowTypes(triageState.flowType);

    return {
      config: {
        personalData: {
          ownerName: getClientName(clientPersonalInfoData),
          segment: personalData.segment,
          dateOfBirth: clientPersonalInfoData.birthDate,
          citizenshipStatus: clientPersonalInfoData.citizenshipStatus,
        },
        decedentInfo: decedentInfo,
        featureToggles: featureToggles,
        triage: triageState,
        openAccountState: openAccountState,
        selectedAccs: selectedClientAccounts,
        gifting: { ...giftingState, selectedPositions },
        estateInfo: estateInfo,
        trust: trustState,
        trustTaxInfo: trustTaxInfoState,
        tcfTrust: tcfTrustState,
        employment: {
          associations: employmentFeature?.associations,
          employmentStatus: employmentFeature?.employmentStatus,
        },
        mailingAddress: address.personalAddress?.mailingAddress,
        streetAddress: address.personalAddress?.streetAddress,
        phone,
        beneficiaries: beneficiaryState,
      },
      receiptDTO: createReceiptDTO(triageState, selectedClientAccounts, receiptDTO),
      transactionType: transactionType,
      otpVerificationStatus: otpVerificationStatus,
      daEnrollPageHtml: enrollPageHTML || undefined,
    };
  },
);

export const getITSubmitPayload = createSelector(
  selectClientName,
  selectClientSegment,
  getFeatureToggleState,
  getInternalTransferState,
  selectInternalTransferReceiptData(),
  getOpenAccountState,
  selectSelectedPositions,
  selectOTPVerificationStatus,

  (
    ownerName,
    segment,
    featureToggles,
    internalTransferState,
    receiptDTO: InternalTransferReceiptDTO,
    openAccountState,
    selectedPositions,
    otpVerificationStatus,
  ): InternalTransferSubmit => {
    return {
      itConfig: {
        personalData: { ownerName, segment },
        featureToggles: featureToggles,
        internalTransfer: internalTransferState,
        openAccountState: openAccountState,
        selectedPositions: selectedPositions,
      },
      receiptDTO: receiptDTO,
      transactionType: Co6TransactionTypes.internalTransfer,
      otpVerificationStatus: otpVerificationStatus,
    };
  },
);
